export type {
  AggregationGranularity,
  DynamicMetricGroupSpec,
  KpiTableRow,
  KpiWeekCell,
  MetricDefinition,
  MetricFormatType,
  MetricGroupSpec,
  ResolvedKpiPrefs,
  StaticMetricGroup,
} from './types';

export { KPI_METRIC_GROUPS, type KpiTableSlug } from './metric-groups';

export { getGaRevenueMetrics, getRevenueBreakdownMetrics, getTotalBusinessRevenueMetrics } from './dynamic-groups';

export { WHOLESALE_METRIC_SERVICES, WHOLESALE_SERVICE_NAMES, filterWholesaleMetrics } from './wholesale';

export {
  BRAND_VALUE_BREAKDOWN_MEASURES,
  REVENUE_KPI_MEASURES,
  buildBrandValueBreakdownDateRange,
  buildBrandValueBreakdownQueryForRange,
  buildRevenueKpiDateRange,
  buildRevenueKpiQueryForRange,
  filterFbReachMeasuresByGranularity,
} from './queries';

export {
  buildKpiRows,
  mergeWithComparison,
  type BuildRowsArgs,
  type CubeRow,
  type KpiRowOut,
  type MergeArgs,
  type MergedRow,
} from './merge';

export { resolveKpiPrefs, type CompanyMetadataLike, type PrefResolveInput } from './prefs';

export {
  REPORT_CONFIG_VERSION,
  isBuiltinGroup,
  isCustomGroup,
  parseReportConfig,
  type BuiltinReportGroup,
  type CustomReportGroup,
  type GroupVisualization,
  type ReportConfig,
  type ReportGroup,
} from './report-config';

export {
  CALCULATED_METRIC_CATEGORY,
  CUSTOM_KEY_PREFIX,
  CUSTOM_METRIC_CATEGORY,
  METRIC_CATALOG,
  buildMetricCatalog,
  calculatedMetricCatalogEntries,
  customMetricCatalogEntries,
  getMetricCatalogEntry,
  isCustomMetricKey,
  resolveCatalogMetrics,
  sourceForMetricKey,
  sourcesForMetricKeys,
  type CustomMetricLike,
  type MetricCatalogEntry,
  type MetricSource,
} from './metric-catalog';

export {
  INITIATIVE_BREAKOUT_CATEGORY,
  INITIATIVE_CATEGORY_KEY_PREFIX,
  INITIATIVE_CATEGORY_LABELS,
  INITIATIVE_CATEGORY_VALUES,
  INITIATIVE_METRIC_FIELDS,
  INITIATIVE_TYPE_KEY_PREFIX,
  PREDEFINED_INITIATIVE_TYPES,
  UNASSIGNED_INITIATIVE_TYPE,
  buildInitiativeCategoryMetrics,
  buildInitiativeTypeMetrics,
  initiativeCategoryMetricKey,
  initiativeTypeCatalogEntries,
  initiativeTypeMetricKey,
  isInitiativeMetricKey,
  type InitiativeCategoryValue,
  type InitiativeMetricFieldName,
  type InitiativeTypeLike,
} from './initiative-metrics';

export {
  AMAZON_CONTRIBUTION_KEY,
  AMAZON_CONTRIBUTION_MARGIN_KEY,
  AMAZON_FINANCIALS_DEFAULTS,
  COMPUTED_METRICS,
  COMPUTED_METRIC_PREFIX,
  computeAmazonContribution,
  computeAmazonContributionMargin,
  getComputedMetric,
  isComputedMetricKey,
  resolveAmazonFinancials,
  type AmazonFinancials,
  type ComputedMetricDef,
  type SourceGetter,
} from './computed-metrics';

export {
  AMAZON_ADS_SPEND_SOURCE_KEY,
  AMAZON_REVENUE_SOURCE_KEY,
  METRIC_AVAILABILITY_RULES,
  SEARCH_CONSOLE_SOURCE_KEY,
  availabilityWarningsForCalc,
  availabilityWarningsForMetric,
  buildAvailabilityIndex,
  collectCalcRefKeys,
  formatAvailabilityDate,
  resolveComparisonDate,
  type AvailabilityIndex,
  type AvailabilityPoint,
  type AvailabilityRowSet,
  type AvailabilityRule,
  type SourceBounds,
} from './data-availability';

export {
  CALC_KEY_PREFIX,
  MAX_CALC_CHAIN_DEPTH,
  MAX_EXPR_DEPTH,
  calcDefsById,
  calcFormatToMetricType,
  calcIdsReferencing,
  collectCalcLeafKeys,
  evaluateCalcExpr,
  isCalculatedMetricKey,
  isValidCalcExpr,
  parseCalcExpr,
  parseCalculatedMetricDef,
  parseCalculatedMetrics,
  type CalcDefsById,
  type CalcExpr,
  type CalcFormat,
  type CalcOperator,
  type CalcRefGetter,
  type CalculatedMetricDef,
} from './calculated-metrics';

export { BUILTIN_DEFAULT_GROUP_ORDER, DEFAULT_REPORT_CONFIG, DEFAULT_REPORT_ID, DEFAULT_REPORT_NAME } from './default-report';
export { toFiniteNumber } from './numeric';

export {
  bucketValue,
  comparisonShiftForWindows,
  deriveBucketEnd,
  isAdditiveFormat,
  resolveBucketValue,
  shiftComparisonStart,
  type BucketGranularity,
  type BucketValueSource,
  type CustomMetricValueBucket,
  type ResolvedBucketValue,
} from './custom-metric-buckets';

export {
  AD_SPEND_SUBTYPES,
  AD_SPEND_SUBTYPE_LABELS,
  CUSTOM_METRIC_TYPES,
  CUSTOM_METRIC_TYPE_LABELS,
  REVENUE_CHANNEL_SUBTYPES,
  REVENUE_CHANNEL_SUBTYPE_LABELS,
  TYPED_RATIO_RECOMPUTES,
  applyTypedColumnDeltas,
  buildTypedColumnDeltas,
  foldTargetsFor,
  foldTargetsForMetric,
  hasAnyTypedDelta,
  isCustomMetricType,
  isValidTypeSubtype,
  subtypesForType,
  typedCustomMetricLabel,
  type AdSpendSubtype,
  type CustomMetricType,
  type RatioRecompute,
  type RevenueChannelSubtype,
  type TypedColumnDeltas,
  type TypedCustomMetricLike,
  type TypedFoldTargets,
  type TypedSeriesFold,
} from './typed-custom-metrics';

export {
  DEFAULT_WEEK_START_DAY,
  WEEK_START_DAYS,
  WEEK_START_DAY_INDEX,
  daysSinceWeekStart,
  isWeekStartDay,
  resolveWeekStartDay,
  weekGranularityName,
  type WeekStartDay,
} from './week';

import { KPI_METRIC_GROUPS, type KpiTableSlug } from './metric-groups';
import type { MetricDefinition, MetricGroupSpec, ResolvedKpiPrefs } from './types';

export function getGroupSpec(slug: KpiTableSlug): MetricGroupSpec {
  return KPI_METRIC_GROUPS[slug] as MetricGroupSpec;
}

export function resolveGroupMetrics(slug: KpiTableSlug, prefs: ResolvedKpiPrefs): MetricDefinition[] {
  const group = getGroupSpec(slug);
  // `isDynamic` is optional on `StaticMetricGroup`, so `if (group.isDynamic)`
  // doesn't narrow the union (TS keeps `group` as the union, hiding `metrics`).
  // Key off `resolveMetrics`, which only the dynamic variant declares.
  if ('resolveMetrics' in group) {
    return group.resolveMetrics(prefs);
  }
  return [...group.metrics];
}

export function listKpiTables(): Array<{
  slug: KpiTableSlug;
  title: string;
  isDynamic: boolean;
}> {
  return (Object.keys(KPI_METRIC_GROUPS) as KpiTableSlug[]).map((slug) => {
    const group = getGroupSpec(slug);
    return {
      slug,
      title: group.title,
      isDynamic: group.isDynamic === true,
    };
  });
}
